import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from './button';
import { SimpleSelect } from './select';

export const PAGINATION_PAGE_SIZES = [10, 20, 50] as const;

export type PaginationPageSize = (typeof PAGINATION_PAGE_SIZES)[number];

export interface PaginationProps {
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: PaginationPageSize) => void;
  page: number;
  pageSize: PaginationPageSize;
  total: number;
}

export const Pagination: FC<PaginationProps> = ({
  onPageChange,
  onPageSizeChange,
  page,
  pageSize,
  total,
}) => {
  const { t } = useTranslation();
  const minPageSize = PAGINATION_PAGE_SIZES[0];
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  if (total <= minPageSize) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-col gap-3 text-xs text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-2">
        <span className="shrink-0">{t('pagination.rowsPerPage')}</span>
        <SimpleSelect
          aria-label={t('pagination.rowsPerPage')}
          options={PAGINATION_PAGE_SIZES.map((size) => ({
            label: String(size),
            value: String(size),
          }))}
          triggerClassName="h-8 w-auto min-w-[4.5rem]"
          value={String(pageSize)}
          onValueChange={(value) => {
            onPageSizeChange(Number(value) as PaginationPageSize);
          }}
        />
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <span>
            {t('pagination.pageOf', { page: safePage, total: totalPages })}
          </span>
          <div className="inline-flex gap-1">
            <Button
              aria-label={t('pagination.previous')}
              disabled={safePage <= 1}
              size="icon-sm"
              type="button"
              variant="ghost"
              onClick={() => onPageChange(Math.max(1, safePage - 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              aria-label={t('pagination.next')}
              disabled={safePage >= totalPages}
              size="icon-sm"
              type="button"
              variant="ghost"
              onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

Pagination.displayName = 'Pagination';
